<i18n locale="en">
{
  "labels": {
    "label": "Title",
    "options": "Options",
    "choices": "Choices",
    "required": "Required"
  }
}
</i18n>

<template>
  <div class="select-editor flex-col gap-8">
    <OwnInputContainer
      :errors="mapErrorMessage(formState.label.$errors)"
      :label="t('labels.label')"
    >
      <OwnInput v-model="label" :placeholder="t('labels.label')" />
    </OwnInputContainer>
    <div class="flex-col gap-3">
      <OwnType
        :text="t('labels.options')"
        color="primary"
        variant="title-regular"
      />
      <OwnToggle v-model="required" :label="t('labels.required')" control />
    </div>
    <div class="flex-col gap-3">
      <OwnType
        :text="t('labels.choices')"
        color="primary"
        variant="title-regular"
      />
      <Reorderable
        v-slot="{ item }"
        v-model="options"
        class="space-y-3"
        item-key="id"
        @reorder="onReorder"
      >
        <OwnOption el="div">
          <div class="flex-row align-center justify-between">
            <div class="flex-row gap-3 align-center">
              <div
                class="select-editor__drag-handle drag-handle flex-col align-center"
              >
                <PhDotsSixVertical class="text-color-secondary" />
              </div>
              <OwnType
                :text="item.label"
                class="select-editor__option-text"
                variant="title-regular"
              />
            </div>
          </div>
        </OwnOption>
      </Reorderable>
    </div>
  </div>
</template>

<script setup>
import { PhDotsSixVertical } from '@phosphor-icons/vue'
import { useVuelidate } from '@vuelidate/core'
import { required as requiredRule } from '@vuelidate/validators'
import debounce from 'lodash/debounce'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useMappedGetter } from '@/store'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnOption } from '@/ui/OwnOption'
import { OwnToggle } from '@/ui/OwnToggle'
import { OwnType } from '@/ui/OwnType'
import Reorderable from '@/ui/reorderable/Reorderable.vue'
import { reorder } from '@/utils/helpers'
import { mapErrorMessage } from '@/utils/vuelidate'

import { LocationSelectCommands } from '../commands/LocationSelectCommands'

const props = defineProps({
  /**
   * Id getting passed to the editor component
   */
  id: { required: true, type: String },
})

const { t } = useI18n()

const label = ref('')
const options = ref([])
const required = ref(false)

const formState = useVuelidate(
  {
    label: { required: requiredRule },
    options: { required: requiredRule },
    required: { required: requiredRule },
  },
  { label, options, required }
)

const settingInitialValues = ref(false)

const blockById = useMappedGetter('formBuilder/blockById')
const brandLocations = useMappedGetter('core/brand/active/locations')

const currentBlockParams = computed(() => {
  return blockById.value(props.id)?.params
})

const locationOptions = computed(() => {
  if (!brandLocations.value) return []

  return brandLocations.value.map((location) => ({
    id: location.id,
    label: location.name,
  }))
})

const formValues = computed(() => ({
  label: label.value,
  options: options.value,
  required: required.value,
}))

const onFormControlUpdateDebounced = debounce(async (controlName, update) => {
  if (settingInitialValues.value) return

  await LocationSelectCommands.updateBlock({
    targetId: props.id,
    update: { params: { [controlName]: update } },
  })
}, 250)

const setInitialFormValues = (formValues) => {
  settingInitialValues.value = true

  for (const [controlName, initialValue] of Object.entries(formValues)) {
    if (controlName === 'label') {
      label.value = initialValue
    } else if (controlName === 'options') {
      options.value = initialValue
    } else if (controlName === 'required') {
      required.value = initialValue
    }
  }

  nextTick(() => {
    settingInitialValues.value = false
  })
}

const onFormControlUpdate = async (controlName, update) => {
  await LocationSelectCommands.updateBlock({
    targetId: props.id,
    update: { params: { [controlName]: update } },
  })
}

const onReorder = async ({ newIndex, oldIndex }) => {
  const newOptions = reorder(formValues.value.options, oldIndex, newIndex)
  await onFormControlUpdate('options', newOptions)
}

watch(
  () => label.value,
  (newVal) => {
    if (!settingInitialValues.value) {
      onFormControlUpdateDebounced('label', newVal)
    }
  }
)

watch(
  () => options.value,
  (newVal) => {
    if (!settingInitialValues.value) {
      onFormControlUpdateDebounced('options', newVal)
    }
  }
)

watch(
  () => required.value,
  (newVal) => {
    if (!settingInitialValues.value) {
      onFormControlUpdateDebounced('required', newVal)
    }
  }
)

onMounted(() => {
  const currentParams = currentBlockParams.value
  const existingOptions = currentParams?.options || []

  // preserve any existing customized labels, but always defer to active locations
  const effectiveOptions = locationOptions.value.map(
    ({ id, label: locationLabel }) => ({
      id,
      label:
        existingOptions.find((option) => option.id === id)?.label ??
        locationLabel,
    })
  )

  setInitialFormValues({
    ...currentParams,
    options: effectiveOptions,
  })
})
</script>
<style lang="scss" scoped>
.select-editor {
  &__drag-handle {
    cursor: grab;
  }

  &__option-text {
    user-select: none;
  }
}
</style>
